import { analytics } from "../analytics";
import * as events from "../analytics/events";
import {
  identifyAnalyticsUser,
  resetAnalyticsUser,
} from "../analytics/identify";
import { setClarityTag } from "../analytics/clarity";

/**
 * Redux middleware that listens to dispatched actions and fires
 * corresponding analytics events. This keeps tracking decoupled
 * from UI components.
 */
export const analyticsMiddleware = (store) => (next) => (action) => {
  const result = next(action);

  switch (action.type) {
    // ─── Auth Events ──────────────────────────────────────────────

    case "auth/login/fulfilled": {
      const user = store.getState().auth.user;
      identifyAnalyticsUser(user);
      analytics.track(events.LOGIN_COMPLETED, {
        method: "email",
      });
      break;
    }

    case "auth/setAuthFromOAuth": {
      const user = action.payload?.user;
      if (user) {
        identifyAnalyticsUser(user);
        analytics.track(events.LOGIN_COMPLETED, {
          method: "google",
        });
      }
      break;
    }

    case "auth/login/rejected": {
      analytics.track(events.LOGIN_FAILED, {
        error_type: action.payload || "unknown",
      });
      break;
    }

    case "auth/completeRegistration/fulfilled": {
      const user = store.getState().auth.user;
      identifyAnalyticsUser(user);
      analytics.track(events.SIGNUP_COMPLETED, {
        method: "email",
      });
      break;
    }

    case "auth/logout/fulfilled": {
      analytics.track(events.LOGOUT);
      resetAnalyticsUser();
      break;
    }

    case "auth/initialize/fulfilled": {
      // Re-identify user on app reload if already logged in
      const user = store.getState().auth.user;
      if (user) {
        identifyAnalyticsUser(user);
      }
      break;
    }

    case "auth/forgotPassword/fulfilled": {
      analytics.track(events.PASSWORD_RESET_REQUESTED);
      break;
    }

    case "auth/resetPassword/fulfilled": {
      analytics.track(events.PASSWORD_RESET_COMPLETED);
      break;
    }

    case "auth/verifyEmail/fulfilled": {
      analytics.track(events.EMAIL_VERIFIED);
      analytics.setUserProperties({ email_verified: true });
      break;
    }

    case "auth/verifyPhone/fulfilled": {
      analytics.track(events.PHONE_VERIFIED);
      analytics.setUserProperties({ phone_verified: true });
      break;
    }

    case "auth/addPaymentMethod/fulfilled": {
      const methods = store.getState().auth.paymentMethods;
      analytics.track(events.BOOKING_PAYMENT_METHOD_ADDED, {
        is_first_method: methods.length === 1,
      });
      analytics.setUserProperties({ has_payment_method: true });
      break;
    }

    // ─── Booking Funnel Events ────────────────────────────────────

    case "bookingFlow/initializeBooking": {
      const { serviceSlug, serviceName } = action.payload;
      analytics.track(events.BOOKING_STARTED, {
        service_slug: serviceSlug,
        service_name: serviceName,
      });
      setClarityTag("service", serviceSlug);
      break;
    }

    case "bookingFlow/answerQuestion": {
      const { questionId } = action.payload;
      const state = store.getState().bookingFlow;
      analytics.track(events.BOOKING_QUESTION_ANSWERED, {
        service_slug: state.serviceSlug,
        question_id: questionId,
      });
      break;
    }

    case "bookingFlow/completeServiceDetails": {
      const state = store.getState().bookingFlow;
      analytics.track(events.BOOKING_STEP_COMPLETED, {
        step: "service-details",
        service_slug: state.serviceSlug,
      });
      break;
    }

    case "bookingFlow/setDateTime": {
      const state = store.getState().bookingFlow;
      const { date, time } = action.payload;
      analytics.track(events.BOOKING_DATETIME_SELECTED, {
        service_slug: state.serviceSlug,
        day_of_week: date
          ? new Date(date).toLocaleDateString("en", { weekday: "long" })
          : undefined,
        time_slot: time,
      });
      analytics.track(events.BOOKING_STEP_COMPLETED, {
        step: "datetime",
        service_slug: state.serviceSlug,
      });
      break;
    }

    case "bookingFlow/setAddress":
    case "bookingFlow/selectSavedAddress": {
      const state = store.getState().bookingFlow;
      const isSaved = action.type === "bookingFlow/selectSavedAddress";
      analytics.track(events.BOOKING_ADDRESS_SELECTED, {
        service_slug: state.serviceSlug,
        is_saved_address: isSaved,
      });
      analytics.track(events.BOOKING_STEP_COMPLETED, {
        step: "address",
        service_slug: state.serviceSlug,
      });
      break;
    }

    case "bookingFlow/fetchPriceEstimate/fulfilled": {
      const state = store.getState().bookingFlow;
      const estimate = state.estimate;
      analytics.track(events.BOOKING_ESTIMATE_VIEWED, {
        service_slug: state.serviceSlug,
        min_price: estimate?.min,
        max_price: estimate?.max,
        confidence: estimate?.confidence,
        is_fallback: estimate?.isFallback || false,
      });
      analytics.track(events.BOOKING_STEP_COMPLETED, {
        step: "estimate",
        service_slug: state.serviceSlug,
      });
      break;
    }

    case "bookingFlow/fetchPriceEstimate/rejected": {
      const state = store.getState().bookingFlow;
      analytics.track(events.ESTIMATE_FETCH_FAILED, {
        service_slug: state.serviceSlug,
      });
      break;
    }

    case "bookingFlow/submit/fulfilled": {
      const state = store.getState().bookingFlow;
      analytics.track(events.BOOKING_SUBMITTED, {
        service_slug: state.serviceSlug,
        booking_id: state.submittedBooking?.id,
      });
      analytics.track(events.BOOKING_STEP_COMPLETED, {
        step: "review",
        service_slug: state.serviceSlug,
      });
      break;
    }

    case "bookingFlow/submit/rejected": {
      const state = store.getState().bookingFlow;
      analytics.track(events.BOOKING_SUBMISSION_FAILED, {
        service_slug: state.serviceSlug,
        error: action.payload || "unknown",
      });
      break;
    }

    case "bookingFlow/loadSavedProgress": {
      analytics.track(events.BOOKING_PROGRESS_RESTORED, {
        service_slug: action.payload?.serviceSlug,
      });
      break;
    }

    // ─── Provider Events ──────────────────────────────────────────

    case "provider/acceptBookingLink/fulfilled": {
      analytics.track(events.REQUEST_ACCEPTED, {
        request_id: action.meta?.arg,
      });
      break;
    }

    case "provider/declineBookingLink/fulfilled": {
      analytics.track(events.REQUEST_DECLINED, {
        request_id: action.meta?.arg,
      });
      break;
    }

    case "provider/completeJob/fulfilled": {
      analytics.track(events.JOB_COMPLETED, {
        job_id: action.meta?.arg?.jobId,
      });
      break;
    }

    default:
      break;
  }

  return result;
};
